import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'

import { DepartureTransportMeansType } from 'features/declaration/models/schemas/commonConsignmentSchemas'
import { TransitOperationFormValues } from 'features/declaration/models/schemas/transitOperationFormSchema'
import { excludeDeleted } from 'shared/lib/common-util'
import useSoftDeleteFieldArray from 'shared/rhf/useSoftDeleteFieldArray'
import useCodelist, { createTypeOfIdentificationsByModeOfTransportFilter } from 'features/declaration/codelist/api/useCodelist'
import useCodelistOptions from 'features/declaration/codelist/components/useCodelistOptions'
import { blankItemDepartureTransportMeans } from 'features/declaration/models/blankItems'
import R0855Validator from 'features/declaration/form/validations/R0855'
import E1103Validator from 'features/declaration/form/validations/E1103'
import FormCollapsibleColumn from 'components/p5/FormCollapsibleColumn'
import FormFieldArray from 'components/p5/FormFieldArray'
import FormField from 'components/p5/FormField'
import FormSelect from 'components/p5/FormSelect'
import Input from 'components/p5/Input'
import useDeclarationFieldRequirements
  from 'features/declaration/form/components/useDeclarationFieldRequirements'

const scope = 'departureTransportMeans'

type DepartureTransportMeansProps = {
  inlandModeOfTransport: string
}

function DepartureTransportMeans({ inlandModeOfTransport }: Readonly<DepartureTransportMeansProps>) {
  const {
    control,
    getValues,
    setValue,
  } = useFormContext<TransitOperationFormValues>()

  const { t } = useTranslation()

  const {
    visibleFields,
    softRemove,
    appendWithSequence,
    fields,
  } = useSoftDeleteFieldArray<TransitOperationFormValues, DepartureTransportMeansType>({
    control,
    name: scope,
  })

  const [, countriesOptions] = useCodelist('COUNTRIES')

  const inlandTransportIdentificationsFilterPredicate = useMemo(
    () => createTypeOfIdentificationsByModeOfTransportFilter(inlandModeOfTransport),
    [inlandModeOfTransport],
  )
  const {
    options: inlandTransportIdentificationsOptions,
    isSuccess: isInlandTransportIdentificationsLoaded,
  } = useCodelistOptions('TRANSPORT_IDENTIFICATIONS', inlandTransportIdentificationsFilterPredicate)

  useEffect(() => {
    if (!isInlandTransportIdentificationsLoaded) {
      return
    }

    const departureTransportMeans = getValues(scope)

    departureTransportMeans.forEach((item, index) => {
      if (!inlandModeOfTransport || !inlandTransportIdentificationsOptions
        .find((option) => option.value === item.typeOfIdentification)) {
        const typeOfIdentificationPath = `departureTransportMeans.${index}.typeOfIdentification` as const
        setValue(typeOfIdentificationPath, '', {
          shouldDirty: true,
          shouldTouch: true,
        })
      }
    })
  }, [getValues, inlandModeOfTransport, inlandTransportIdentificationsOptions, isInlandTransportIdentificationsLoaded, setValue])

  const paths = useMemo(
    () => [
      scope,
      ...visibleFields.map((f) => `${scope}.${f.index}.typeOfIdentification`),
      ...visibleFields.map((f) => `${scope}.${f.index}.identificationNumber`),
      ...visibleFields.map((f) => `${scope}.${f.index}.nationality`),
    ],
    [visibleFields],
  )
  const fieldRequirements = useDeclarationFieldRequirements<TransitOperationFormValues>('transitOperation', paths)

  const departureTransportMeansMultiplicity = useMemo(() => R0855Validator
    .getDepartureTransportMeansMultiplicity(inlandModeOfTransport), [inlandModeOfTransport])
  const isAddButtonDisabled = fieldRequirements.departureTransportMeans?.requirement === 'forbidden'
    || fields.filter(excludeDeleted).length >= departureTransportMeansMultiplicity

  return (
    <section className="py-3">
      <FormCollapsibleColumn
        columnId="declaration-deprture-transport"
        headingLabel={t('declaration.p5.departureTransportMeans')}
        fieldRule={fieldRequirements.departureTransportMeans}
      >
        {visibleFields.map((fieldWithIndex) => {
          const departureTransportMeans = fieldWithIndex.field
          const innerItemScope = `${scope}.${fieldWithIndex.index}` as const

          return (
            <FormFieldArray
              key={`departureTransportMeans_${departureTransportMeans.id}`}
              titleKey="declaration.p5.sequenceNumber"
              removeItem={() => softRemove(fieldWithIndex.index)}
              sequenceNumber={departureTransportMeans.sequenceNumber + 1}
              hidden={departureTransportMeans.deleted}
            >
              <FormField
                labelKey="declaration.p5.typeOfIdentification"
                fieldRule={fieldRequirements[`${innerItemScope}.typeOfIdentification`]}
              >
                <FormSelect
                  field={`${innerItemScope}.typeOfIdentification`}
                  labelKey="declaration.p5.typeOfIdentification"
                  options={inlandTransportIdentificationsOptions}
                  isDisabled={!inlandModeOfTransport}
                  type="sync"
                  fieldRule={fieldRequirements[`${innerItemScope}.typeOfIdentification`]}
                />
              </FormField>

              <FormField
                labelKey="declaration.p5.identificationNumber"
                fieldRule={fieldRequirements[`${innerItemScope}.identificationNumber`]}
              >
                <Input
                  field={`${innerItemScope}.identificationNumber`}
                  labelKey="declaration.p5.identificationNumber"
                  type="text"
                  maxLength={E1103Validator.getIdentificationNumberMaxLength()}
                  placeholder={t('declaration.p5.identificationNumber')}
                  fieldRule={fieldRequirements[`${innerItemScope}.identificationNumber`]}
                />
              </FormField>

              <FormField
                labelKey="declaration.p5.nationality"
                fieldRule={fieldRequirements[`${innerItemScope}.nationality`]}
              >
                <FormSelect
                  field={`${innerItemScope}.nationality`}
                  labelKey="declaration.p5.nationality"
                  options={countriesOptions}
                  type="sync"
                  fieldRule={fieldRequirements[`${innerItemScope}.nationality`]}
                />
              </FormField>
            </FormFieldArray>
          )
        })}
        <div className="row">
          <div className={`col-12 text-end ${isAddButtonDisabled ? 'cursor--not-allowed' : ''}`}>
            <button
              type="button"
              className="btn btn-sm btn-primary mt-2"
              onClick={() => appendWithSequence(blankItemDepartureTransportMeans)}
              disabled={isAddButtonDisabled}
            >
              <i className="fa fa-plus me-2" />
              {t('declaration.p5.addNewTransportMeans')}
            </button>
          </div>
        </div>
      </FormCollapsibleColumn>
    </section>
  )
}

export default DepartureTransportMeans
